import { isEmpty } from 'lodash-es';

import { Props as AddressLookupProps } from 'components/AddressLookup';
import { Props as AutoCompleteProps } from 'components/AutoComplete';
import * as api from 'utils/api';

/**
 * API Response for retriving list of addresses matching search critiriea
 */
type GetAddressResponse = {
  /**
   * Number of records in response
   */
  totalRecords: number;
  /**
   * List of all matching address records
   */
  records: AddressLookupProps.Interfaces.ApiShortAddress[];
};

export default class AddressService {
  /**
   * api request adapter object
   */
  private adapter: api.Adapter;
  /**
   * Static constant of search endpoint
   */
  static searchUri = '/adapter/v1/cwp/address/addresses';

  constructor(baseURL: string) {
    this.adapter = new api.Adapter({
      baseURL,
      responseType: 'json',
      headers: {
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });
    // This binding maintains the value of this inside these methods during future calls.
    this.getAddressDetails = this.getAddressDetails.bind(this);
    this.getAddressesLookup = this.getAddressesLookup.bind(this);
  }

  /**
   * Rertrieve a list of addresses
   * @param queryString string to search
   */
  async getAddressesLookup(queryString: string): Promise<AutoCompleteProps.OptionType[]> {
    const response = await this.adapter.getValue<GetAddressResponse>(queryString);
    return response.data.records
      .filter(record => !isEmpty(record.id))
      .map(record => {
        return {
          label: record.addressLine,
          value: record.id
        };
      });
  }

  /**
   * Retrieve the full details of the address
   * @param id unique id for the address
   */
  async getAddressDetails(
    id: string,
    country: string
  ): Promise<AddressLookupProps.Interfaces.ApiFullAddress> {
    const url = `${AddressService.searchUri}/${encodeURIComponent(id)}?country=${country}`;
    const response = await this.adapter.getValue<AddressLookupProps.Interfaces.ApiFullAddress>(url);
    return response.data;
  }
}
